import React, { useState, useEffect, useRef } from 'react';
import { competitionDb } from '../utils/competitionDb';
import config from '../config';
import './CompetitionDialog.css';

interface CompetitionDialogProps {
  isOpen: boolean;
  syncStatus?: 'none' | 'syncing' | 'synced' | 'error';
  activeTeamId?: number | null;
  onClose: () => void;
  onSelectPlayers: (p1: any, p2: any) => void;
}

const CompetitionDialog: React.FC<CompetitionDialogProps> = ({ isOpen, syncStatus, activeTeamId, onClose, onSelectPlayers }) => {
  const [step, setStep] = useState<'agenda' | 'players' | 'poule-selection' | 'settings'>('agenda');
  const [activeTab, setActiveTab] = useState<'agenda' | 'stand' | 'spelers'>('agenda');
  const [allMatches, setAllMatches] = useState<any[]>([]);
  const [pouleInfo, setPouleInfo] = useState<any>(null);
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [allPlayers, setAllPlayers] = useState<any[]>([]);
  const [allPoules, setAllPoules] = useState<any[]>([]);
  const [teamSchedules, setTeamSchedules] = useState<any[]>([]);
  const [standings, setStandings] = useState<any[]>([]);
  const [poulePlayerStats, setPoulePlayerStats] = useState<any[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [playerSortField, setPlayerSortField] = useState<string>('position');
  const [playerSortAsc, setPlayerSortAsc] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const agendaRef = useRef<HTMLDivElement>(null);

  const toggleTeamFilter = (teamId: number) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const toggleAllTeams = () => {
    if (selectedTeamIds.length === teams.length) {
      setSelectedTeamIds([]);
    } else {
      setSelectedTeamIds(teams.map(t => t.id));
    }
  };

  const filteredSchedule = selectedTeamIds.length === 0
    ? allMatches
    : allMatches.filter(m => selectedTeamIds.includes(m.home_team_id) || selectedTeamIds.includes(m.away_team_id));

  // BYE-rondes: als er op 1 team gefilterd wordt, vul de agenda aan met
  // 'BYE'-kaarten voor rondes zonder wedstrijd.
  const scheduleWithByes = React.useMemo(() => {
    if (selectedTeamIds.length !== 1 || filteredSchedule.length === 0) return filteredSchedule;
    const maxRound = Math.max(...allMatches.map(m => Number(m.round) || 0));
    if (maxRound <= 0) return filteredSchedule;
    const matchByRound = new Map(filteredSchedule.map(m => [Number(m.round), m]));
    const result: any[] = [];
    for (let r = 1; r <= maxRound; r++) {
      if (matchByRound.has(r)) {
        result.push(matchByRound.get(r));
      } else {
        // BYE: zet een placeholder in de agenda
        result.push({ __bye: true, round: r });
      }
    }
    return result;
  }, [filteredSchedule, allMatches, selectedTeamIds]);

  const saveSettings = () => {
    if (pouleInfo) {
      localStorage.setItem(`competition_teams_${pouleInfo.id}`, JSON.stringify(selectedTeamIds));
    }
    setStep('agenda');
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Reload data when sync completes
  useEffect(() => {
    if (isOpen && syncStatus === 'synced') {
      loadData();
    }
  }, [syncStatus, isOpen]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
        const info = await competitionDb.getPouleInfo();
        const matches = await competitionDb.getSchedule();
        const t = await competitionDb.getTeams();
        const p = await competitionDb.getPlayers();
        const ts = await competitionDb.getTeamSchedules();
        const st = await competitionDb.getStandings();
        const pps = await competitionDb.getPoulePlayerStats();
        setTeamSchedules(ts || []);
        setStandings(st || []);
        
        let activePps = pps || [];
        // On-demand fallback: als er lokaal nog geen player stats zijn opgeslagen maar wel op de server beschikbaar zijn
        if (activePps.length === 0 && info?.id) {
          try {
            const ppsRes = await fetch(`${config.apiUrl}/api/v1/competition/player-stats/${info.id}`);
            const ppsData = await ppsRes.json();
            if (ppsData.success && Array.isArray(ppsData.stats) && ppsData.stats.length > 0) {
              activePps = ppsData.stats;
              // Bewaar direct in IndexedDB
              await competitionDb.savePoulePlayerStats(activePps);
            }
          } catch (fetchErr) {
            console.warn('[COMP] Kon poule player stats niet on-demand fetchen:', fetchErr);
          }
        }
        setPoulePlayerStats(activePps);
        
        if (!info || matches.length === 0) {
            if (activeTeamId) {
                console.log('[COMP] No local data but activeTeamId present, waiting for sync...');
                // Blijf even wachten, sync is waarschijnlijk bezig
                return;
            }
            console.log('[COMP] No local data found, switching to selection step');
            setStep('poule-selection');
            await fetchAvailablePoules();
        } else {
            setPouleInfo(info);
            // Sort matches chronologically by match_date, then by round
            const sortedMatches = [...matches].sort((a, b) => {
              const dateA = new Date(a.match_date).getTime() || 0;
              const dateB = new Date(b.match_date).getTime() || 0;
              if (dateA !== dateB) return dateA - dateB;
              return (Number(a.round) || 0) - (Number(b.round) || 0);
            });
            setAllMatches(sortedMatches);
            setTeams(t);
            setAllPlayers(p);
            
            // Initial filter: load from localStorage or use activeTeamId
            const storedTeams = localStorage.getItem(`competition_teams_${info.id}`);
            if (storedTeams) {
              setSelectedTeamIds(JSON.parse(storedTeams));
            } else if (activeTeamId && selectedTeamIds.length === 0) {
              setSelectedTeamIds([activeTeamId]);
            }
            
            setStep('agenda');
        }
    } catch (e) {
        console.error('[COMP] Load error', e);
        setError('Kon lokale data niet laden.');
        setStep('poule-selection');
        await fetchAvailablePoules();
    } finally {
        setIsLoading(false);
    }
  };

  // Auto-scroll naar de eerstvolgende/huidige wedstrijd zodra de agenda zichtbaar en gerenderd is
  useEffect(() => {
    if (isOpen && step === 'agenda' && activeTab === 'agenda' && scheduleWithByes.length > 0) {
      // Wacht tot de DOM elements gemonteerd en getekend zijn
      const timer = setTimeout(() => {
        scrollToToday();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, step, activeTab, scheduleWithByes]);

  const fetchAvailablePoules = async () => {
    setIsLoading(true);
    try {
        const res = await fetch(`${config.apiUrl}/api/v1/competition/poules`);
        const data = await res.json();
        if (data.success) {
            setAllPoules(data.poules);
        } else {
            setError('Kon poules niet ophalen van server.');
        }
    } catch (e) {
        setError('Netwerkfout bij ophalen poules.');
    } finally {
        setIsLoading(false);
    }
  };

  const handlePouleSelect = async (poule: any) => {
    setIsLoading(true);
    setError(null);
    try {
        console.log(`[COMP] Manueel syncen voor poule: ${poule.name} (${poule.id})`);
        const res = await fetch(`${config.apiUrl}/api/v1/competition/sync/${poule.id}`);
        const data = await res.json();
        
        if (data.success) {
            setSelectedTeamIds([]);
            await competitionDb.saveSyncData(data.data);
            // Herlaad alles
            await loadData();
        } else {
            setError(data.message || 'Synchronisatie mislukt.');
        }
    } catch (e) {
        setError('Fout bij synchroniseren met server.');
    } finally {
        setIsLoading(false);
    }
  };


  const getLocalTodayString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Converteert een match_date waarde (ISO-string of Date) naar lokale YYYY-MM-DD.
  // PostgreSQL DATE-kolommen komen via de pg-driver binnen als UTC-midnight ISO-strings
  // (bijv. "2026-09-24T22:00:00.000Z" voor een wedstrijd op vr 25 sep in CEST/+02:00).
  // Door getFullYear/Month/Date te gebruiken (lokale tijd) krijgen we de juiste datum.
  const toLocalDateString = (dateVal: string | Date | null | undefined): string => {
    if (!dateVal) return '';
    const d = new Date(dateVal as string);
    if (isNaN(d.getTime())) return String(dateVal).slice(0, 10);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const scrollToToday = () => {
    if (!agendaRef.current) return;
    const today = getLocalTodayString();
    // Gebruik data-match-date attribuut — index-onafhankelijk, werkt ook met BYE-entries
    const matchElems = agendaRef.current.querySelectorAll<HTMLElement>('.agenda-item[data-match-date]');
    
    let target: HTMLElement | null = null;
    for (let i = 0; i < matchElems.length; i++) {
        const elem = matchElems[i];
        const matchDate = elem.dataset.matchDate || '';
        // Wedstrijden worden in de avond gespeeld: op datum-X is de eerstvolgende
        // wedstrijd die op datum-X zelf (of de eerstvolgende daarna)
        if (matchDate >= today) {
            target = elem;
            break;
        }
    }
    
    if (target) {
        // Bereken scroll positie via getBoundingClientRect (betrouwbaar ongeacht positioned ancestors)
        const container = agendaRef.current;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
        container.scrollTop = scrollTop;
    }
  };
    

  const handleMatchSelect = (match: any) => {
    setSelectedMatch(match);
    setP1Selection(null);
    setP2Selection(null);
    setStep('players');
  };

  const [p1Selection, setP1Selection] = useState<any>(null);
  const [p2Selection, setP2Selection] = useState<any>(null);

  const parseRank = (val: string | number | null | undefined): number => {
    if (val === null || val === undefined) return Infinity;
    const str = String(val).trim();
    if (str === '' || str === '---' || str === '—') return Infinity;
    const num = Number(str.replace(/[^0-9.]/g, ''));
    return isNaN(num) || num <= 0 ? Infinity : num;
  };

  // Hulpfunctie om spelers strikt op speelsterkte te sorteren
  const sortPlayersByStrength = (playersList: any[]) => {
    return [...playersList].sort((a, b) => {
      const rankA = parseRank(a.countryRanking ?? a.country_rank ?? a.countryRank);
      const rankB = parseRank(b.countryRanking ?? b.country_rank ?? b.countryRank);
      if (rankA !== rankB) {
        return rankA - rankB; // Ascending: rank 1 voor rank 2, ongerangschikt onderaan
      }
      const sbnA = parseRank(a.sbnPlayerLevel ?? a.sbn_player_level);
      const sbnB = parseRank(b.sbnPlayerLevel ?? b.sbn_player_level);
      if (sbnA !== sbnB) {
        return sbnA - sbnB;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Bepaal de geplande speelposities (-1- t/m -4-) voor Hoenderdaal 1
  const plannedPositionsMap = React.useMemo(() => {
    if (!selectedMatch || !teamSchedules || teamSchedules.length === 0) {
      return new Map<string, number>();
    }

    const roundNum = Number(selectedMatch.round);
    if (!roundNum) return new Map<string, number>();

    // Zoek uit of een van beide teams Hoenderdaal 1 is
    const hoenderdaalTeamId = [selectedMatch.home_team_id, selectedMatch.away_team_id].find(tid => {
      const teamObj = teams.find(t => t.id === tid);
      return teamObj && teamObj.name && teamObj.name.toLowerCase().includes('hoenderdaal');
    });

    if (!hoenderdaalTeamId) return new Map<string, number>();

    // Filter schedules voor dit team en deze ronde met status 'Sp'
    const roundSchedules = teamSchedules.filter(
      ts => ts.team_id === hoenderdaalTeamId && Number(ts.round) === roundNum && (ts.status === 'Sp' || ts.status === 'sp')
    );

    if (roundSchedules.length === 0) return new Map<string, number>();

    // Match de geplande spelers met de allPlayers objecten
    const hdPlayers = allPlayers.filter(p => p.team_id === hoenderdaalTeamId);
    const playingPlayers = hdPlayers.filter(p => {
      return roundSchedules.some(rs => {
        if (rs.player_id && p.player_id && String(rs.player_id) === String(p.player_id)) return true;
        if (rs.sbn_id && p.sbn_id && String(rs.sbn_id).trim() === String(p.sbn_id).trim()) return true;
        if (rs.player_name && p.name && p.name.toLowerCase().includes(rs.player_name.toLowerCase())) return true;
        return false;
      });
    });

    // Sorteer de 4 geplande spelers op speelsterkte: sterkste = 1, daarna 2, 3, 4
    const sortedPlaying = sortPlayersByStrength(playingPlayers);
    const posMap = new Map<string, number>();
    sortedPlaying.forEach((p, idx) => {
      const pos = idx + 1;
      if (p.id) posMap.set(String(p.id), pos);
      if (p.player_id) posMap.set(String(p.player_id), pos);
      if (p.sbn_id) posMap.set(String(p.sbn_id), pos);
    });

    return posMap;
  }, [selectedMatch, teamSchedules, teams, allPlayers]);

  const getPlayersByTeam = (teamId: number) => {
    return allPlayers
      .filter(p => p.team_id === teamId)
      .sort((a, b) => {
        // 1. Spelers die gepland staan (-1- t/m -4-) komen eerst
        const posA = plannedPositionsMap.get(String(a.id)) ||
          (a.player_id && plannedPositionsMap.get(String(a.player_id))) ||
          (a.sbn_id && plannedPositionsMap.get(String(a.sbn_id))) || Infinity;
        const posB = plannedPositionsMap.get(String(b.id)) ||
          (b.player_id && plannedPositionsMap.get(String(b.player_id))) ||
          (b.sbn_id && plannedPositionsMap.get(String(b.sbn_id))) || Infinity;

        if (posA !== posB) {
          return posA - posB; // Positie 1 t/m 4 vooraan
        }

        // 2. Vervolgens overige spelers gesorteerd op speelsterkte (ranking/SBN level/naam)
        const rankA = parseRank(a.countryRanking ?? a.country_rank ?? a.countryRank);
        const rankB = parseRank(b.countryRanking ?? b.country_rank ?? b.countryRank);
        if (rankA !== rankB) {
          return rankA - rankB; // Ascending: rank 1 before rank 2, unranked last
        }
        const sbnA = parseRank(a.sbnPlayerLevel ?? a.sbn_player_level);
        const sbnB = parseRank(b.sbnPlayerLevel ?? b.sbn_player_level);
        if (sbnA !== sbnB) {
          return sbnA - sbnB;
        }
        return (a.name || '').localeCompare(b.name || '');
      });
  };

  useEffect(() => {
    if (step === 'players' && p1Selection && p2Selection) {
      handleFinish();
    }
  }, [p1Selection, p2Selection, step]);

  const handleFinish = async () => {
    if (!p1Selection || !p2Selection) return;
    
    // Direct return to main screen, scraping info is already in DB
    const normalizedP1 = {
      ...p1Selection,
      sbnPlayerLevel: p1Selection.sbnPlayerLevel ?? p1Selection.sbn_player_level,
      countryRanking: p1Selection.countryRanking ?? p1Selection.country_rank ?? p1Selection.countryRank,
      worldRanking: p1Selection.worldRanking ?? p1Selection.world_rank ?? p1Selection.worldRank
    };
    const normalizedP2 = {
      ...p2Selection,
      sbnPlayerLevel: p2Selection.sbnPlayerLevel ?? p2Selection.sbn_player_level,
      countryRanking: p2Selection.countryRanking ?? p2Selection.country_rank ?? p2Selection.countryRank,
      worldRanking: p2Selection.worldRanking ?? p2Selection.world_rank ?? p2Selection.worldRank
    };
    onSelectPlayers(normalizedP1, normalizedP2);
  };

  if (!isOpen) return null;

  return (
    <div className="competition-overlay">
      <div className="competition-modal">
        <div className="competition-header">
          <div className="header-left">
            <h2>
              {step === 'agenda' && (syncStatus === 'syncing' ? 'VERVERSEN...' : 'AGENDA')}
              {step === 'settings' && (
                <span className="header-players-title">
                  <button 
                    type="button" 
                    className="header-back-btn" 
                    onClick={() => setStep('agenda')}
                    title="Terug naar agenda"
                  >‹</button>
                  INSTELLINGEN
                </span>
              )}
              {step === 'players' && (
                <span className="header-players-title">
                  <button 
                    type="button" 
                    className="header-back-btn" 
                    onClick={() => setStep('agenda')}
                    title="Terug naar agenda"
                  >‹</button>
                  SPELERS SELECTEREN
                </span>
              )}
              {step === 'poule-selection' && (
                <span className="header-players-title">
                  <button 
                    type="button" 
                    className="header-back-btn" 
                    onClick={() => setStep(pouleInfo ? 'settings' : 'agenda')}
                    title="Terug"
                  >‹</button>
                  POULE SELECTEREN
                </span>
              )}
            </h2>
            {step === 'agenda' && (
              <div className="header-tab-nav">
                <button
                  className={`header-tab-btn ${activeTab === 'agenda' ? 'active' : ''}`}
                  onClick={() => setActiveTab('agenda')}
                >AGENDA</button>
                <button
                  className={`header-tab-btn ${activeTab === 'stand' ? 'active' : ''}`}
                  onClick={() => setActiveTab('stand')}
                >STAND</button>
                <button
                  className={`header-tab-btn ${activeTab === 'spelers' ? 'active' : ''}`}
                  onClick={() => setActiveTab('spelers')}
                >SPELERS</button>
              </div>
            )}
          </div>
          <div className="header-actions-right">
            {step === 'agenda' && (
              <button 
                className="header-settings-btn" 
                onClick={() => setStep('settings')} 
                title="Competitie instellingen & team filters"
              >
                ⚙️
              </button>
            )}
            <button className="close-btn" onClick={onClose} disabled={isLoading}>×</button>
          </div>
        </div>

        {step === 'settings' && (
            <div className="competition-body settings-step">
                <div className="settings-section">
                    <h3>Poule Selectie</h3>
                    <div className="current-poule-display">
                        <div className="poule-info-main">
                            <span className="poule-name">{pouleInfo?.name}</span>
                            <span className="poule-sub">{pouleInfo?.klasse} - {pouleInfo?.afdeling}</span>
                        </div>
                        <button className="change-poule-btn" onClick={() => setStep('poule-selection')}>Wijzig Poule</button>
                    </div>
                </div>

                <div className="settings-section">
                    <h3>Teams in Agenda tonen</h3>
                    <div className="team-filter-large">
                        <label className={`filter-option-large ${selectedTeamIds.length === teams.length ? 'active' : ''}`}>
                            <input 
                                type="checkbox" 
                                checked={selectedTeamIds.length === teams.length} 
                                onChange={toggleAllTeams} 
                            />
                            <div className="checkbox-ui"></div>
                            <span>Alle Teams</span>
                        </label>
                        {teams.map(team => (
                            <label key={team.id} className={`filter-option-large ${selectedTeamIds.includes(team.id) ? 'active' : ''}`}>
                                <input 
                                    type="checkbox" 
                                    checked={selectedTeamIds.includes(team.id)} 
                                    onChange={() => toggleTeamFilter(team.id)} 
                                />
                                <div className="checkbox-ui"></div>
                                <span>{team.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="footer-actions">
                    <button className="back-btn" onClick={() => setStep('agenda')}>Annuleren</button>
                    <button className="confirm-btn" onClick={saveSettings}>Opslaan</button>
                </div>
            </div>
        )}

        {step === 'poule-selection' && (
            <div className="competition-body">
                <div className="instruction-text">
                    {error ? <span className="error-text">⚠️ {error}</span> : 'Geen actieve poule gevonden. Selecteer handmatig:'}
                </div>
                {isLoading ? (
                    <div className="loading-state">Laden van poules...</div>
                ) : (
                    <div className="poule-list">
                        {allPoules.map(p => (
                            <div key={p.id} className="poule-option" onClick={() => handlePouleSelect(p)}>
                                <div className="poule-title">{p.name}</div>
                                <div className="poule-meta">{p.competition_name} | {p.klasse} - {p.afdeling}</div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="footer-actions">
                    <button className="back-btn" onClick={fetchAvailablePoules}>Ververs Lijst</button>
                </div>
            </div>
        )}

        {step === 'agenda' && (
          <div className="competition-body">
            {activeTab === 'agenda' && (
              <div className="agenda-list" ref={agendaRef}>
                {scheduleWithByes.map((match, idx) => {
                  if (match.__bye) {
                    return (
                      <div key={`bye-${match.round}`} className="agenda-item bye-round">
                        <div className="match-meta">
                          <span className="round">Ronde {match.round}</span>
                          <span className="date bye-label">Vrij (BYE)</span>
                        </div>
                        <div className="match-teams bye-teams">
                          <div className="team bye-text">— Geen wedstrijd —</div>
                        </div>
                      </div>
                    );
                  }
                  const localMatchDate = toLocalDateString(match.match_date);
                  const isToday = localMatchDate === getLocalTodayString();
                  return (
                    <div
                      key={idx}
                      className={`agenda-item ${isToday ? 'today' : ''}`}
                      onClick={() => handleMatchSelect(match)}
                      data-match-date={localMatchDate}
                    >
                      <div className="match-meta">
                        <span className="round">Ronde {match.round}</span>
                        <span className="date">{new Date(match.match_date).toLocaleDateString('nl-NL', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'Europe/Amsterdam' })}</span>
                      </div>
                      <div className="match-teams">
                        <div className="team home">{match.home_team_name}</div>
                        <div className="vs">vs</div>
                        <div className="team away">{match.away_team_name}</div>
                      </div>
                    </div>
                  );
                })}
                {scheduleWithByes.length === 0 && <div className="empty-state">Geen wedstrijden gevonden voor de geselecteerde filters.</div>}
              </div>
            )}

            {activeTab === 'stand' && (
              <div className="stand-container">
                {standings.length === 0 ? (
                  <div className="empty-state">Stand nog niet beschikbaar voor deze poule.</div>
                ) : (
                  <div className="standings-table-wrapper">
                    <table className="standings-table">
                      <thead>
                        <tr>
                          <th className="th-pos">#</th>
                          <th className="th-team">Team</th>
                          <th className="th-num" title="Gespeeld">GS</th>
                          <th className="th-num" title="Gewonnen">W</th>
                          <th className="th-num" title="Gelijk">O</th>
                          <th className="th-num" title="Verloren">V</th>
                          <th className="th-ratio" title="Wedstrijden W-V">Wedstr</th>
                          <th className="th-ratio" title="Games W-V">Games</th>
                          <th className="th-ratio" title="Punten W-V">Pt-diff</th>
                          <th className="th-pts" title="Totaal Punten">Pt</th>
                          <th className="th-form" title="Vorm">Vorm</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((st: any) => {
                          const isOwnTeam = Boolean(
                            (activeTeamId && st.team_id === activeTeamId) ||
                            (st.team_name && st.team_name.toLowerCase().includes('hoenderdaal 1')) ||
                            (st.team_name && st.team_name.toLowerCase().includes('hoenderdaal health center 1'))
                          );
                          return (
                            <tr key={st.id || st.team_id || st.position} className={`standings-row ${isOwnTeam ? 'own-team-row' : ''}`}>
                              <td className="td-pos">
                                <span className={`pos-badge pos-${st.position}`}>{st.position}</span>
                              </td>
                              <td className="td-team">
                                <span className="team-name-text">{st.team_name}</span>
                              </td>
                              <td className="td-num">{st.played ?? 0}</td>
                              <td className="td-num stat-won">{st.won ?? 0}</td>
                              <td className="td-num stat-draw">{st.draw ?? 0}</td>
                              <td className="td-num stat-lost">{st.lost ?? 0}</td>
                              <td className="td-ratio">{st.matches_wl || '—'}</td>
                              <td className="td-ratio">{st.games_wl || '—'}</td>
                              <td className="td-ratio">{st.points_wl || '—'}</td>
                              <td className="td-pts font-bold">{st.points ?? 0}</td>
                              <td className="td-form">
                                <div className="form-badges">
                                  {(st.history || []).map((res: string, hIdx: number) => {
                                    const resUpper = (res || '').toUpperCase();
                                    const badgeClass = resUpper === 'W' ? 'form-w' : resUpper === 'V' ? 'form-v' : 'form-o';
                                    return (
                                      <span key={hIdx} className={`form-badge ${badgeClass}`}>
                                        {resUpper}
                                      </span>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'spelers' && (() => {
              // Verrijk poulePlayerStats met rankingdata uit allPlayers
              // Match op naam (lowercased, gedeeltelijk)
              const enrichedStats = poulePlayerStats.map((ps: any) => {
                // Probeer te matchen met een speler uit allPlayers op naam
                const nameLower = (ps.player_name || '').toLowerCase();
                const matched = allPlayers.find(p => {
                  const pn = (p.name || '').toLowerCase();
                  return pn === nameLower ||
                    pn.includes(nameLower) ||
                    nameLower.includes(pn) ||
                    (nameLower.split(' ').length > 1 &&
                      pn.split(' ').length > 1 &&
                      pn.split(' ').slice(-1)[0] === nameLower.split(' ').slice(-1)[0] &&
                      pn.split(' ')[0][0] === nameLower.split(' ')[0][0]);
                });
                return {
                  ...ps,
                  level: matched?.level ?? null,
                  grade: matched?.nlSquashGrade ?? matched?.grade ?? null,
                  worldRanking: matched?.worldRanking ?? matched?.world_rank ?? null,
                  countryRanking: matched?.countryRanking ?? matched?.country_rank ?? null,
                  sbnPlayerLevel: matched?.sbnPlayerLevel ?? matched?.sbn_player_level ?? null,
                  club: matched?.club ?? ps.club_name ?? null,
                };
              });

              const handlePlayerSort = (field: string) => {
                if (playerSortField === field) {
                  setPlayerSortAsc(prev => !prev);
                } else {
                  setPlayerSortField(field);
                  setPlayerSortAsc(true);
                }
              };

              const sortedStats = [...enrichedStats].sort((a: any, b: any) => {
                let valA: any = null;
                let valB: any = null;

                switch (playerSortField) {
                  case 'position':
                    valA = a.position;
                    valB = b.position;
                    break;
                  case 'name':
                    valA = (a.player_name || '').toLowerCase();
                    valB = (b.player_name || '').toLowerCase();
                    break;
                  case 'club':
                    valA = (a.club || a.club_name || '').toLowerCase();
                    valB = (b.club || b.club_name || '').toLowerCase();
                    break;
                  case 'countryRanking':
                    valA = a.countryRanking !== null && a.countryRanking !== undefined ? Number(a.countryRanking) : null;
                    valB = b.countryRanking !== null && b.countryRanking !== undefined ? Number(b.countryRanking) : null;
                    break;
                  case 'level':
                    valA = a.level !== null && a.level !== undefined ? Number(a.level) : null;
                    valB = b.level !== null && b.level !== undefined ? Number(b.level) : null;
                    break;
                  case 'grade':
                    valA = a.grade !== null && a.grade !== undefined ? Number(a.grade) : null;
                    valB = b.grade !== null && b.grade !== undefined ? Number(b.grade) : null;
                    break;
                  case 'worldRanking':
                    valA = a.worldRanking !== null && a.worldRanking !== undefined ? Number(a.worldRanking) : null;
                    valB = b.worldRanking !== null && b.worldRanking !== undefined ? Number(b.worldRanking) : null;
                    break;
                  case 'sbnPlayerLevel':
                    valA = a.sbnPlayerLevel !== null && a.sbnPlayerLevel !== undefined ? Number(a.sbnPlayerLevel) : null;
                    valB = b.sbnPlayerLevel !== null && b.sbnPlayerLevel !== undefined ? Number(b.sbnPlayerLevel) : null;
                    break;
                  case 'wl':
                    valA = Number(a.wins ?? 0) - Number(a.losses ?? 0);
                    valB = Number(b.wins ?? 0) - Number(b.losses ?? 0);
                    break;
                  default:
                    valA = a.position;
                    valB = b.position;
                }

                // Nulls/undefined altijd onderaan sorteren
                const isANull = valA === null || valA === undefined || (typeof valA === 'number' && isNaN(valA)) || valA === '';
                const isBNull = valB === null || valB === undefined || (typeof valB === 'number' && isNaN(valB)) || valB === '';
                if (isANull && isBNull) return 0;
                if (isANull) return 1;
                if (isBNull) return -1;

                if (typeof valA === 'string' && typeof valB === 'string') {
                  const cmp = valA.localeCompare(valB, 'nl');
                  return playerSortAsc ? cmp : -cmp;
                }

                return playerSortAsc ? (valA < valB ? -1 : valA > valB ? 1 : 0) : (valA > valB ? -1 : valA < valB ? 1 : 0);
              });

              const renderSortArrow = (field: string) => {
                if (playerSortField !== field) {
                  return <span className="sort-indicator neutral">↕</span>;
                }
                return <span className="sort-indicator active">{playerSortAsc ? '▲' : '▼'}</span>;
              };

              return (
                <div className="spelers-container">
                  {enrichedStats.length === 0 ? (
                    <div className="empty-state">
                      <div style={{ marginBottom: '8px' }}>Spelers-statistieken nog niet geladen.</div>
                      <button 
                        className="back-btn" 
                        style={{ marginTop: '12px', padding: '6px 16px', cursor: 'pointer' }}
                        onClick={async () => {
                          if (pouleInfo?.id) {
                            setIsLoading(true);
                            try {
                              const res = await fetch(`${config.apiUrl}/api/v1/competition/sync/${pouleInfo.id}`);
                              const data = await res.json();
                              if (data.success && data.data) {
                                await competitionDb.saveSyncData(data.data);
                                await loadData();
                              }
                            } catch (e) {
                              console.error(e);
                            } finally {
                              setIsLoading(false);
                            }
                          }
                        }}
                      >
                        🔄 Gegevens Nu Ophalen
                      </button>
                      <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '8px' }}>
                        Of draai <code>python scrape_POULE_STANDINGS.py</code> om de spelersdata te updaten.
                      </div>
                    </div>
                  ) : (
                    <div className="spelers-table-wrapper">
                      <table className="spelers-table">
                        <thead>
                          <tr>
                            <th className="th-pos sortable-th" onClick={() => handlePlayerSort('position')} title="Klik om te sorteren op positie">
                              <span className="th-content"># {renderSortArrow('position')}</span>
                            </th>
                            <th className="th-name sortable-th" onClick={() => handlePlayerSort('name')} title="Klik om te sorteren op naam">
                              <span className="th-content">Naam {renderSortArrow('name')}</span>
                            </th>
                            <th className="th-club sortable-th" onClick={() => handlePlayerSort('club')} title="Klik om te sorteren op club">
                              <span className="th-content">Club {renderSortArrow('club')}</span>
                            </th>
                            <th className="th-num sortable-th" onClick={() => handlePlayerSort('countryRanking')} title="Klik om te sorteren op NLD Country Ranking">
                              <span className="th-content">NLD {renderSortArrow('countryRanking')}</span>
                            </th>
                            <th className="th-num sortable-th" onClick={() => handlePlayerSort('level')} title="Klik om te sorteren op SquashLevels Rating">
                              <span className="th-content">Level {renderSortArrow('level')}</span>
                            </th>
                            <th className="th-num sortable-th" onClick={() => handlePlayerSort('grade')} title="Klik om te sorteren op NL Squash Grade">
                              <span className="th-content">Grade {renderSortArrow('grade')}</span>
                            </th>
                            <th className="th-num sortable-th" onClick={() => handlePlayerSort('worldRanking')} title="Klik om te sorteren op World Ranking">
                              <span className="th-content">World {renderSortArrow('worldRanking')}</span>
                            </th>
                            <th className="th-num sortable-th" onClick={() => handlePlayerSort('sbnPlayerLevel')} title="Klik om te sorteren op SBN Speelsterkte">
                              <span className="th-content">SBN {renderSortArrow('sbnPlayerLevel')}</span>
                            </th>
                            <th className="th-wl sortable-th" onClick={() => handlePlayerSort('wl')} title="Klik om te sorteren op Winst - Verlies">
                              <span className="th-content">W - V {renderSortArrow('wl')}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedStats.map((ps: any, idx: number) => {
                            const wl_total = ps.total_matches || (ps.wins + ps.losses);
                            const isOwnTeam = allPlayers.some(p => {
                              const pn = (p.name || '').toLowerCase();
                              const psn = (ps.player_name || '').toLowerCase();
                              return pn === psn || pn.includes(psn) || psn.includes(pn);
                            });
                            return (
                              <tr key={ps.id || ps.sbn_player_id || idx} className={`spelers-row${isOwnTeam ? ' own-team-row' : ''}`}>
                                <td className="td-pos">
                                  <span className={`pos-badge pos-${ps.position || idx + 1}`}>{ps.position || idx + 1}</span>
                                </td>
                                <td className="td-name">{ps.player_name}</td>
                                <td className="td-club">{ps.club || ps.club_name || '—'}</td>
                                <td className="td-num">{ps.countryRanking || '—'}</td>
                                <td className="td-num">{ps.level ? Number(ps.level).toFixed(1) : '—'}</td>
                                <td className="td-num">{ps.grade || '—'}</td>
                                <td className="td-num">{ps.worldRanking || '—'}</td>
                                <td className="td-num">{ps.sbnPlayerLevel || '—'}</td>
                                <td className="td-wl">
                                  <span className="wl-display">
                                    <span className="wl-wins">{ps.wins ?? 0}</span>
                                    <span className="wl-sep"> – </span>
                                    <span className="wl-losses">{ps.losses ?? 0}</span>
                                    {wl_total > 0 && <span className="wl-total"> ({wl_total})</span>}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {step === 'players' && selectedMatch && (
          <div className="competition-body players-step">
            {isLoading && <div className="loading-overlay">Scraping rankings...</div>}

            <div className="selection-grid">
              <div className="team-column">
                <div className="player-list">
                  {getPlayersByTeam(selectedMatch.home_team_id).map((p, idx) => {
                    const sbn = p.sbnPlayerLevel ?? p.sbn_player_level;
                    const countryRank = p.countryRanking ?? p.country_rank ?? p.countryRank;
                    const hasSbn = sbn !== undefined && sbn !== null && String(sbn).trim() !== '' && String(sbn).trim() !== '---' && String(sbn).trim() !== '-';
                    const hasCountryRank = countryRank !== undefined && countryRank !== null && String(countryRank).trim() !== '' && String(countryRank).trim() !== '---' && String(countryRank).trim() !== '-';
                    const isCaptain = Boolean(p.isCaptain ?? p.is_captain);
                    const lineupPos = plannedPositionsMap.get(String(p.id)) ||
                      (p.player_id && plannedPositionsMap.get(String(p.player_id))) ||
                      (p.sbn_id && plannedPositionsMap.get(String(p.sbn_id)));

                    return (
                      <div 
                        key={p.id} 
                        className={`player-option ${p1Selection?.id === p.id ? 'selected' : ''}`}
                        onClick={() => setP1Selection(p)}
                      >
                        <div className="player-card-top-bar">
                          {lineupPos ? (
                            <span className="lineup-position-badge" title={`Geplande speelpositie: ${lineupPos}`}>
                              {lineupPos}
                            </span>
                          ) : (
                            <span className="player-card-number">{idx + 1}</span>
                          )}
                          <span className={`player-stat-badge ${hasCountryRank ? 'rank-badge' : 'rank-badge empty-rank-badge'}`} title="Country Ranking (NLD)">
                            <span className="stat-label">NLD:</span>
                            <span className="stat-value">{hasCountryRank ? countryRank : '-'}</span>
                          </span>
                          <span className="player-card-sbn" title="SBN Player Level">
                            {hasSbn ? sbn : '-'}
                          </span>
                        </div>
                        <div className="player-option-name">
                          {p.name}
                          {isCaptain && <span className="captain-badge" title="Team Captain">C</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="team-column">
                <div className="player-list">
                  {getPlayersByTeam(selectedMatch.away_team_id).map((p, idx) => {
                    const sbn = p.sbnPlayerLevel ?? p.sbn_player_level;
                    const countryRank = p.countryRanking ?? p.country_rank ?? p.countryRank;
                    const hasSbn = sbn !== undefined && sbn !== null && String(sbn).trim() !== '' && String(sbn).trim() !== '---' && String(sbn).trim() !== '-';
                    const hasCountryRank = countryRank !== undefined && countryRank !== null && String(countryRank).trim() !== '' && String(countryRank).trim() !== '---' && String(countryRank).trim() !== '-';
                    const isCaptain = Boolean(p.isCaptain ?? p.is_captain);
                    const lineupPos = plannedPositionsMap.get(String(p.id)) ||
                      (p.player_id && plannedPositionsMap.get(String(p.player_id))) ||
                      (p.sbn_id && plannedPositionsMap.get(String(p.sbn_id)));

                    return (
                      <div 
                        key={p.id} 
                        className={`player-option ${p2Selection?.id === p.id ? 'selected' : ''}`}
                        onClick={() => setP2Selection(p)}
                      >
                        <div className="player-card-top-bar">
                          {lineupPos ? (
                            <span className="lineup-position-badge" title={`Geplande speelpositie: ${lineupPos}`}>
                              {lineupPos}
                            </span>
                          ) : (
                            <span className="player-card-number">{idx + 1}</span>
                          )}
                          <span className={`player-stat-badge ${hasCountryRank ? 'rank-badge' : 'rank-badge empty-rank-badge'}`} title="Country Ranking (NLD)">
                            <span className="stat-label">NLD:</span>
                            <span className="stat-value">{hasCountryRank ? countryRank : '-'}</span>
                          </span>
                          <span className="player-card-sbn" title="SBN Player Level">
                            {hasSbn ? sbn : '-'}
                          </span>
                        </div>
                        <div className="player-option-name">
                          {p.name}
                          {isCaptain && <span className="captain-badge" title="Team Captain">C</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionDialog;
